// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import type { Observation, Patient } from '@medplum/fhirtypes';
import {
  HEIGHT_LOINC_CODE,
  LOINC_SYSTEM,
  UCUM_SYSTEM,
  VITAL_SIGNS_CATEGORY_SYSTEM,
  WEIGHT_LOINC_CODE,
} from '../constants/patient-profile';
import { withTenantScope } from './tenant';

const VITAL_SIGNS_CATEGORY = {
  coding: [{ system: VITAL_SIGNS_CATEGORY_SYSTEM, code: 'vital-signs', display: 'Vital Signs' }],
};

const HEIGHT_CODE = {
  coding: [{ system: LOINC_SYSTEM, code: HEIGHT_LOINC_CODE, display: 'Body height' }],
};

const WEIGHT_CODE = {
  coding: [{ system: LOINC_SYSTEM, code: WEIGHT_LOINC_CODE, display: 'Body weight' }],
};

const BLOOD_PRESSURE_CODE = '85354-9';
const HEART_RATE_CODE = '8867-4';
const RESPIRATORY_RATE_CODE = '9279-1';

export interface BloodPressureValue {
  systolic?: number;
  diastolic?: number;
}

/** Parses `5'9"`, `5ft 9in`, or `5'9` to cm (1 decimal). Returns undefined on bad input. */
export function parseHeightImperialToCm(value: string): number | undefined {
  const match = value.trim().match(/^(\d+)\s*(?:ft|')\s*(\d+(?:\.\d+)?)?\s*(?:in|")?$/i);
  if (!match) {
    return undefined;
  }
  const feet = Number.parseFloat(match[1]);
  const inches = match[2] ? Number.parseFloat(match[2]) : 0;
  return Math.round((feet * 30.48 + inches * 2.54) * 10) / 10;
}

/** Formats a cm value as `5'9"`. Returns empty string for undefined/non-finite. */
export function formatCmToImperial(cm: number | undefined): string {
  if (cm === undefined || !Number.isFinite(cm)) {
    return '';
  }
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches - feet * 12);
  if (inches === 12) {
    return `${feet + 1}'0"`;
  }
  return `${feet}'${inches}"`;
}

/** Parses `165`, `165 lbs`, `165lb` to lbs. Returns undefined on bad input. */
export function parseWeightLbs(value: string): number | undefined {
  const match = value.trim().match(/^(\d+(?:\.\d+)?)\s*(?:lbs?|pounds?)?$/i);
  if (!match) {
    return undefined;
  }
  const lbs = Number.parseFloat(match[1]);
  return Number.isFinite(lbs) ? lbs : undefined;
}

/** lbs → kg (1 decimal). */
export function lbsToKg(lbs: number): number {
  return Math.round(lbs * 0.453592 * 10) / 10;
}

/** kg → lbs (1 decimal). */
export function kgToLbs(kg: number): number {
  return Math.round((kg / 0.453592) * 10) / 10;
}

/** Formats `165` lbs as `165 lbs`. Returns empty string for undefined. */
export function formatLbs(lbs: number | undefined): string {
  if (lbs === undefined || !Number.isFinite(lbs)) {
    return '';
  }
  return `${lbs} lbs`;
}

async function fetchLatestVitalObservation(
  medplum: MedplumClient,
  patientId: string,
  loincCode: string
): Promise<Observation | undefined> {
  return medplum.searchOne('Observation', {
    subject: `Patient/${patientId}`,
    code: `${LOINC_SYSTEM}|${loincCode}`,
    _sort: '-date',
    _count: '1',
  });
}

export async function getLatestHeightCm(medplum: MedplumClient, patientId: string): Promise<number | undefined> {
  const obs = await fetchLatestVitalObservation(medplum, patientId, HEIGHT_LOINC_CODE);
  return obs?.valueQuantity?.value;
}

export async function getLatestWeightKg(medplum: MedplumClient, patientId: string): Promise<number | undefined> {
  const obs = await fetchLatestVitalObservation(medplum, patientId, WEIGHT_LOINC_CODE);
  return obs?.valueQuantity?.value;
}

export async function getLatestBloodPressure(
  medplum: MedplumClient,
  patientId: string
): Promise<BloodPressureValue | undefined> {
  const obs = await fetchLatestVitalObservation(medplum, patientId, BLOOD_PRESSURE_CODE);
  const systolic = obs?.component?.find((component) =>
    component.code?.coding?.some((coding) => coding.code === '8480-6')
  )?.valueQuantity?.value;
  const diastolic = obs?.component?.find((component) =>
    component.code?.coding?.some((coding) => coding.code === '8462-4')
  )?.valueQuantity?.value;
  return systolic !== undefined || diastolic !== undefined ? { systolic, diastolic } : undefined;
}

export async function getLatestHeartRate(medplum: MedplumClient, patientId: string): Promise<number | undefined> {
  const obs = await fetchLatestVitalObservation(medplum, patientId, HEART_RATE_CODE);
  return obs?.valueQuantity?.value;
}

export async function getLatestRespiratoryRate(medplum: MedplumClient, patientId: string): Promise<number | undefined> {
  const obs = await fetchLatestVitalObservation(medplum, patientId, RESPIRATORY_RATE_CODE);
  return obs?.valueQuantity?.value;
}

function createVitalObservation(
  patient: Patient,
  code: string,
  display: string,
  valueQuantity: NonNullable<Observation['valueQuantity']> | undefined,
  component?: Observation['component']
): Observation {
  if (!patient.id) {
    throw new Error('Patient.id required to create vital Observation');
  }
  return withTenantScope({
    resourceType: 'Observation',
    status: 'final',
    category: [VITAL_SIGNS_CATEGORY],
    code: { coding: [{ system: LOINC_SYSTEM, code, display }] },
    subject: { reference: `Patient/${patient.id}` },
    effectiveDateTime: new Date().toISOString(),
    ...(valueQuantity ? { valueQuantity } : {}),
    ...(component ? { component } : {}),
  });
}

export function createBloodPressureObservation(patient: Patient, systolic: number, diastolic: number): Observation {
  return createVitalObservation(patient, BLOOD_PRESSURE_CODE, 'Blood pressure', undefined, [
    {
      code: { coding: [{ system: LOINC_SYSTEM, code: '8480-6', display: 'Systolic blood pressure' }] },
      valueQuantity: { value: systolic, unit: 'mmHg', system: UCUM_SYSTEM, code: 'mm[Hg]' },
    },
    {
      code: { coding: [{ system: LOINC_SYSTEM, code: '8462-4', display: 'Diastolic blood pressure' }] },
      valueQuantity: { value: diastolic, unit: 'mmHg', system: UCUM_SYSTEM, code: 'mm[Hg]' },
    },
  ]);
}

export function createHeartRateObservation(patient: Patient, value: number): Observation {
  return createVitalObservation(patient, HEART_RATE_CODE, 'Heart rate', {
    value,
    unit: 'beats/minute',
    system: UCUM_SYSTEM,
    code: '/min',
  });
}

export function createRespiratoryRateObservation(patient: Patient, value: number): Observation {
  return createVitalObservation(patient, RESPIRATORY_RATE_CODE, 'Respiratory rate', {
    value,
    unit: 'breaths/minute',
    system: UCUM_SYSTEM,
    code: '/min',
  });
}

/** Creates a new height Observation. Vitals are append-only — each edit becomes a new measurement. */
export async function createHeightObservation(
  medplum: MedplumClient,
  patient: Patient,
  heightCm: number
): Promise<Observation> {
  if (!patient.id) {
    throw new Error('Patient.id required to create height Observation');
  }
  const resource: Observation = {
    resourceType: 'Observation',
    status: 'final',
    category: [VITAL_SIGNS_CATEGORY],
    code: HEIGHT_CODE,
    subject: { reference: `Patient/${patient.id}` },
    effectiveDateTime: new Date().toISOString(),
    valueQuantity: { value: heightCm, unit: 'cm', system: UCUM_SYSTEM, code: 'cm' },
  };
  return medplum.createResource<Observation>(withTenantScope(resource));
}

/** Creates a new weight Observation. Vitals are append-only — each edit becomes a new measurement. */
export async function createWeightObservation(
  medplum: MedplumClient,
  patient: Patient,
  weightKg: number
): Promise<Observation> {
  if (!patient.id) {
    throw new Error('Patient.id required to create weight Observation');
  }
  const resource: Observation = {
    resourceType: 'Observation',
    status: 'final',
    category: [VITAL_SIGNS_CATEGORY],
    code: WEIGHT_CODE,
    subject: { reference: `Patient/${patient.id}` },
    effectiveDateTime: new Date().toISOString(),
    valueQuantity: { value: weightKg, unit: 'kg', system: UCUM_SYSTEM, code: 'kg' },
  };
  return medplum.createResource<Observation>(withTenantScope(resource));
}
